/**
 * @fileoverview Scratch extension for text classification using TensorFlow.js models.
 * This extension provides text classification, sentiment analysis,
 * text-to-speech, and speech recognition capabilities.
 */

const BlockType = require("../../extension-support/block-type");
const ArgumentType = require("../../extension-support/argument-type");
const Cast = require("../../util/cast");
const formatMessage = require("format-message");

// Import TensorFlow.js and working models
const tf = require("@tensorflow/tfjs");
const Sentiment = require("sentiment");

/**
 * Icon svg to be displayed at left edge of each extension block, encoded as a data URI.
 * @type {string}
 */
const blockIconURI =
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iNiIgZmlsbD0iIzU3NzNGRiIvPgo8cGF0aCBkPSJNMTIgMTJINTJWMjhIMTJWMTJaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K";

/**
 * Configuration constants for the text classification system.
 * @readonly
 */
const CONFIG = {
    // Audio settings
    LOUDNESS_CHECK_INTERVAL: 100,
    DEFAULT_THRESHOLD: 10,
    MIN_CLASSES: 2,

    // Service URLs
    SYNTHESIS_SERVICE_URL: "https://synthesis-service.scratch.mit.edu/synth",
    TRANSLATION_SERVICE_URL:
        "https://translate-service.scratch.mit.edu/translate",

    // TensorFlow settings
    TENSORFLOW_CLEANUP_DELAY: 200,
};

/**
 * Voice types for text-to-speech synthesis.
 * @readonly
 */
const VOICE_TYPES = [
    { text: "squeak", value: "SQUEAK" },
    { text: "tenor", value: "TENOR" },
    { text: "alto", value: "ALTO" },
    { text: "giant", value: "GIANT" },
];

/**
 * Voice configuration for different synthesis voices.
 * @readonly
 */
const VOICE_CONFIG = {
    SQUEAK: { gender: "female", playbackRate: 1.4 },
    TENOR: { gender: "male", playbackRate: 1.0 },
    ALTO: { gender: "female", playbackRate: 1.0 },
    GIANT: { gender: "male", playbackRate: 0.7 },
};

/**
 * Cached translations to avoid repeated API calls
 * @type {Map<string, string>}
 */
const translationCache = new Map();

/**
 * Check if text appears to be in English (simple heuristic).
 * @param {string} text - Text to check
 * @returns {boolean} True if text appears to be English
 */
const isLikelyEnglish = (text) => {
    // Simple heuristic: if text contains mostly ASCII characters and common English words
    const englishWords = [
        "the",
        "and",
        "or",
        "but",
        "in",
        "on",
        "at",
        "to",
        "for",
        "of",
        "with",
        "by",
        "is",
        "are",
        "was",
        "were",
        "a",
        "an",
    ];
    const asciiRatio =
        text.split("").filter((char) => char.charCodeAt(0) < 128).length /
        text.length;
    const lowerText = text.toLowerCase();
    const hasEnglishWords = englishWords.some((word) =>
        lowerText.includes(word)
    );

    // If mostly ASCII characters, assume it's English
    return asciiRatio > 0.8 || hasEnglishWords;
};

/**
 * Get translation to English from external service.
 * @param {string} text - Text to translate
 * @returns {Promise<string>} Translated text or original if translation fails
 */
const getTranslationToEnglish = async (text) => {
    if (translationCache.has(text)) return translationCache.get(text);

    // Skip translation if text is likely already in English
    if (isLikelyEnglish(text)) {
        translationCache.set(text, text);
        return text;
    }

    try {
        const url = `${
            CONFIG.TRANSLATION_SERVICE_URL
        }?language=en&text=${encodeURIComponent(text)}`;
        const response = await fetch(url, { timeout: 1000 });

        if (!response.ok) {
            console.warn(
                `Translation service error: ${response.status} ${response.statusText}`
            );
            return text;
        }

        const json = await response.json();
        const translated = json.result
            ? json.result.replaceAll("%20", " ")
            : text;
        translationCache.set(text, translated);
        return translated;
    } catch (error) {
        console.warn(`Error fetching translate result! ${error}`);
        return text; // Return original text if translation fails
    }
};

// Initialize TensorFlow.js
tf.ready().then(() => {
    console.log("TensorFlow initialized for Text Classification extension");
});

/**
 * Scratch extension class for text classification and related AI/ML text processing.
 */
class Scratch3TextClassificationBlocks {
    /**
     * Create a new TextClassification extension instance.
     * @param {Runtime} runtime - The Scratch runtime instance
     */
    constructor(runtime) {
        /**
         * The Scratch runtime instance
         * @type {Runtime}
         * @private
         */
        this.runtime = runtime;

        /**
         * User-defined classification labels
         * @type {string[]}
         * @private
         */
        this.labels = [];

        /**
         * Training data for custom model (label -> examples mapping)
         * @type {Map<string, string[]>}
         * @private
         */
        this.modelData = new Map();

        /**
         * Trained custom model predictor function
         * @type {Function|null}
         * @private
         */
        this.customPredictor = null;

        /**
         * Current model identifier for training state management
         * @type {Symbol|null}
         * @private
         */
        this.currentModelIdentifier = null;

        /**
         * Sentiment analysis instance
         * @type {object|null}
         * @private
         */
        this.sentimentAnalyzer = null;

        /**
         * Current sentiment analysis result
         * @type {object|null}
         * @private
         */
        this.currentSentiment = null;

        /**
         * Last recognized speech text
         * @type {string}
         * @private
         */
        this.lastRecognizedSpeech = "";

        /**
         * Current voice setting for text-to-speech
         * @type {string}
         * @private
         */
        this.currentVoice = "SQUEAK";

        /**
         * Current microphone loudness level
         * @type {number}
         * @private
         */
        this.currentLoudness = 0;

        /**
         * Active sound players for speech synthesis
         * @type {Map<string, object>}
         * @private
         */
        this.soundPlayers = new Map();

        // Initialize libraries
        this._initializeLibraries();

        // Register custom button callbacks
        this._registerButtonCallbacks();

        // Clean up sound players when project stops
        this.runtime.on("PROJECT_STOP_ALL", () => {
            this.soundPlayers.forEach((player) => {
                if (player.stop) player.stop();
            });
            this.soundPlayers.clear();
        });
    }

    /**
     * Get extension metadata including blocks, menus, and display information.
     * @returns {object} Extension metadata object
     */
    getInfo() {
        return {
            id: "textclassification",
            name: formatMessage({
                id: "textClassification.categoryName",
                default: "Text Classification",
                description: "Name of the text classification extension",
            }),
            blockIconURI: blockIconURI,
            blocks: [
                // === MODEL TRAINING & MANAGEMENT ===
                "---", // Separator line
                {
                    opcode: "editModelButton",
                    text: formatMessage({
                        id: "textClassification.editModelButton",
                        default: "🔧 Edit Model",
                        description: "Open model editing interface",
                    }),
                    blockType: BlockType.BUTTON,
                    func: "EDIT_TEXT_MODEL",
                },
                {
                    opcode: "saveLoadModelButton",
                    text: formatMessage({
                        id: "textClassification.saveLoadModelButton",
                        default: "💾 Load / Save Model",
                        description: "Open model import/export interface",
                    }),
                    blockType: BlockType.BUTTON,
                    func: "SAVE_LOAD_TEXT_MODEL",
                },
                "---", // Separator line
                {
                    opcode: "addTrainingData",
                    text: formatMessage({
                        id: "textClassification.addTrainingData",
                        default: "add [TEXT] to class [CLASS_NAME]",
                        description:
                            "Add training text to a classification class",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: "textClassification.defaultTrainingText",
                                default: "Enter text or answer block",
                                description: "Default training text",
                            }),
                        },
                        CLASS_NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: "textClassification.defaultClassName",
                                default: "positive",
                                description: "Default class name",
                            }),
                        },
                    },
                },
                {
                    opcode: "trainModel",
                    text: formatMessage({
                        id: "textClassification.trainModel",
                        default: "train classification model",
                        description: "Train the text classification model",
                    }),
                    blockType: BlockType.COMMAND,
                },
                {
                    opcode: "clearTrainingData",
                    text: formatMessage({
                        id: "textClassification.clearTrainingData",
                        default: "clear all training data",
                        description: "Clear all training data",
                    }),
                    blockType: BlockType.COMMAND,
                },

                {
                    opcode: "getTrainingDataCount",
                    text: formatMessage({
                        id: "textClassification.getTrainingDataCount",
                        default: "training examples for [CLASS_NAME]",
                        description:
                            "Get number of training examples for a class",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        CLASS_NAME: {
                            type: ArgumentType.STRING,
                            menu: "model_classes",
                            defaultValue: "",
                        },
                    },
                },
                {
                    opcode: "ifTextMatchesClass",
                    text: formatMessage({
                        id: "textClassification.ifTextMatchesClass",
                        default: "[TEXT] matches [CLASS_NAME] ?",
                        description: "Check if text matches a specific class",
                    }),
                    blockType: BlockType.BOOLEAN,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: "textClassification.defaultText",
                                default: "Enter text or answer block",
                                description: "Default text to classify",
                            }),
                        },
                        CLASS_NAME: {
                            type: ArgumentType.STRING,
                            menu: "model_classes",
                            defaultValue: "",
                        },
                    },
                },
                {
                    opcode: "getModelPrediction",
                    text: formatMessage({
                        id: "textClassification.getModelPrediction",
                        default: "predict class for [TEXT]",
                        description: "Get predicted class for text",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: "textClassification.defaultText",
                                default: "Enter text or answer block",
                                description: "Default text to classify",
                            }),
                        },
                    },
                },
                {
                    opcode: "getModelConfidence",
                    text: formatMessage({
                        id: "textClassification.getModelConfidence",
                        default: "Confidence of predict class for [TEXT]",
                        description:
                            "Get confidence score for text classification",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: "textClassification.defaultText",
                                default: "Enter text or answer block",
                                description: "Default text to classify",
                            }),
                        },
                    },
                },

                // === TEXT ANALYSIS ===
                "---", // Separator line
                {
                    opcode: "sentimentScore",
                    text: formatMessage({
                        id: "textClassification.sentimentScore",
                        default: "Sentiment Score for [TEXT]",
                        description: "Get sentiment analysis score for text",
                    }),
                    blockType: BlockType.REPORTER,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: "TEXT",
                        },
                    },
                },

                // === SPEECH & AUDIO ===
                "---", // Separator line
                {
                    opcode: "speakText",
                    text: formatMessage({
                        id: "textClassification.speakText",
                        default: "speak [TEXT]",
                        description: "Speak text using text-to-speech",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: "Hello",
                        },
                    },
                },
                {
                    opcode: "setVoice",
                    text: formatMessage({
                        id: "textClassification.setVoice",
                        default: "set voice to [VOICE]",
                        description: "Set the voice for text-to-speech",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        VOICE: {
                            type: ArgumentType.STRING,
                            menu: "voices",
                            defaultValue: "SQUEAK",
                        },
                    },
                },

                // Speech recognition blocks
                {
                    opcode: "askSpeechRecognition",
                    text: formatMessage({
                        id: "textClassification.askSpeechRecognition",
                        default: "speak [PROMPT] then listen for response",
                        description: "Speak prompt and listen for speech input",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        PROMPT: {
                            type: ArgumentType.STRING,
                            defaultValue: "How are you?",
                        },
                    },
                },
                {
                    opcode: "getRecognizedSpeech",
                    text: formatMessage({
                        id: "textClassification.getRecognizedSpeech",
                        default: "response",
                        description: "Get the last recognized speech",
                    }),
                    blockType: BlockType.REPORTER,
                },

                // === MICROPHONE DETECTION ===
                "---", // Separator line
                {
                    opcode: "onHeardSound",
                    text: formatMessage({
                        id: "textClassification.onHeardSound",
                        default: "when heard sound > [THRESHOLD]",
                        description:
                            "Trigger when sound level exceeds threshold",
                    }),
                    blockType: BlockType.HAT,
                    arguments: {
                        THRESHOLD: {
                            type: ArgumentType.NUMBER,
                            defaultValue: CONFIG.DEFAULT_THRESHOLD,
                        },
                    },
                },
                {
                    opcode: "getLoudness",
                    text: formatMessage({
                        id: "textClassification.getLoudness",
                        default: "loudness",
                        description: "Get current microphone loudness",
                    }),
                    blockType: BlockType.REPORTER,
                },
            ],
            menus: {
                voices: {
                    acceptReporters: true,
                    items: VOICE_TYPES,
                },
                model_classes: {
                    acceptReporters: false,
                    items: "getLabels",
                },
            },
        };
    }

    /**
     * Get menu items for model classes based on current labels.
     * @returns {Array} Array of menu items for model classes
     */
    getLabels() {
        if (this.labels.length === 0) {
            return [{ text: "No labels", value: "" }];
        }
        return this.labels.map((label) => ({ text: label, value: label }));
    }

    // === Block Methods ===

    /**
     * Handler for Edit Model button.
     * Opens an interactive interface for model management.
     */
    async editModelButton() {
        const modelInfo =
            this.labels.length > 0
                ? `Current model has ${
                      this.labels.length
                  } classes: ${this.labels.join(", ")}`
                : "No model classes defined yet";

        const examples = Array.from(this.modelData.entries())
            .map(([label, data]) => `• ${label}: ${data.length} examples`)
            .join("<br>");

        const trainingInfo = examples || "• No training data yet";

        const content = `
            <div style="margin-bottom: 16px;">
                <strong>${modelInfo}</strong>
            </div>
            <div style="margin-bottom: 20px;">
                <div style="font-weight: 500; margin-bottom: 8px;">Training Data:</div>
                <div style="padding: 8px; background: #e9ecef; border-radius: 4px; font-size: 13px;">
                    ${trainingInfo}
                </div>
            </div>
            <div style="margin-bottom: 16px;">Choose an action:</div>
            <div style="display: flex; flex-direction: column; gap: 8px;">
                <button data-choice="addLabel" style="
                    padding: 12px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;
                    background: white; text-align: left; transition: background 0.2s;
                " onmouseover="this.style.background='#f8f9fa'" onmouseout="this.style.background='white'">
                    ➕ Add a new class label
                </button>
                <button data-choice="clearData" style="
                    padding: 12px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;
                    background: white; text-align: left; transition: background 0.2s;
                " onmouseover="this.style.background='#f8f9fa'" onmouseout="this.style.background='white'">
                    🗑️ Clear all training data
                </button>
                <button data-choice="trainModel" style="
                    padding: 12px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;
                    background: white; text-align: left; transition: background 0.2s;
                " onmouseover="this.style.background='#f8f9fa'" onmouseout="this.style.background='white'">
                    🚀 Train model with current data
                </button>
                <button data-choice="viewStatus" style="
                    padding: 12px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;
                    background: white; text-align: left; transition: background 0.2s;
                " onmouseover="this.style.background='#f8f9fa'" onmouseout="this.style.background='white'">
                    📈 View model status
                </button>
            </div>
        `;

        const choice = await this._showModal({
            title: "📊 Text Classification Model Editor",
            content,
            buttons: [{ text: "Cancel", action: "cancel" }],
        });

        switch (choice) {
            case "addLabel": {
                const newLabel = await this._showInputModal(
                    "Add Class Label",
                    "Enter new class label:",
                    ""
                );
                if (newLabel && newLabel.trim()) {
                    const labelName = newLabel.trim();
                    this.addLabel(labelName);
                    console.log(
                        `Added label: ${labelName}. Current labels:`,
                        this.labels
                    );
                    await this._showModal({
                        title: "Success",
                        content: `✅ Added class "<strong>${labelName}</strong>".<br><br>Use the "add [text] to class [${labelName}]" block to add training examples.<br><br>Current classes: ${this.labels.join(
                            ", "
                        )}`,
                        buttons: [{ text: "OK", action: "ok" }],
                    });
                }
                break;
            }
            case "clearData": {
                const confirmed = await this._showModal({
                    title: "Confirm Clear Data",
                    content:
                        "⚠️ This will delete all training data. Are you sure?",
                    buttons: [
                        { text: "Cancel", action: "cancel" },
                        { text: "Clear Data", action: "confirm" },
                    ],
                });
                if (confirmed === "confirm") {
                    this.clearLabels();
                    await this._showModal({
                        title: "Data Cleared",
                        content: "🗑️ All training data has been cleared.",
                        buttons: [{ text: "OK", action: "ok" }],
                    });
                }
                break;
            }
            case "trainModel": {
                if (this.labels.length < 2) {
                    await this._showModal({
                        title: "Cannot Train",
                        content: "❌ Need at least 2 classes to train a model.",
                        buttons: [{ text: "OK", action: "ok" }],
                    });
                } else {
                    this.buildCustomDeepModel();
                }
                break;
            }
            case "viewStatus": {
                const status = this.customPredictor
                    ? "✅ Trained and ready"
                    : "❌ Not trained yet";
                const totalExamples = Array.from(
                    this.modelData.values()
                ).reduce((sum, arr) => sum + arr.length, 0);

                await this._showModal({
                    title: "Model Status",
                    content: `
                        <div style="display: flex; flex-direction: column; gap: 12px;">
                            <div><strong>Status:</strong> ${status}</div>
                            <div><strong>Classes:</strong> ${this.labels.length}</div>
                            <div><strong>Total Examples:</strong> ${totalExamples}</div>
                        </div>
                    `,
                    buttons: [{ text: "OK", action: "ok" }],
                });
                break;
            }
            default:
                // Cancel or invalid choice
                break;
        }
    }

    /**
     * Handler for Load/Save Model button.
     * Provides comprehensive import/export functionality.
     */
    async saveLoadModelButton() {
        const hasModel = this.labels.length > 0;
        const modelSummary = hasModel
            ? `Current: ${this.labels.length} classes, ${Array.from(
                  this.modelData.values()
              ).reduce((sum, arr) => sum + arr.length, 0)} examples`
            : "No model data available";

        const content = `
            <div style="margin-bottom: 16px;">
                <strong>${modelSummary}</strong>
            </div>
                        <div style="margin-bottom: 16px;">Choose an action:</div>
            <div style="display: flex; flex-direction: column; gap: 8px;">
                <button data-choice="${hasModel ? "exportModel" : ""}" style="
                    padding: 12px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;
                    background: white; text-align: left; transition: background 0.2s;
                    ${!hasModel ? "opacity: 0.5; cursor: not-allowed;" : ""}
                " onmouseover="this.style.background='${
                    !hasModel ? "white" : "#f8f9fa"
                }'" onmouseout="this.style.background='white'" ${
            !hasModel ? "disabled" : ""
        }>
                    💾 Export current model to file
                </button>
                <button data-choice="importModel" style="
                    padding: 12px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;
                    background: white; text-align: left; transition: background 0.2s;
                " onmouseover="this.style.background='#f8f9fa'" onmouseout="this.style.background='white'">
                    📁 Import model from file
                </button>
                <button data-choice="${
                    hasModel ? "exportTrainingData" : ""
                }" style="
                    padding: 12px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;
                    background: white; text-align: left; transition: background 0.2s;
                    ${!hasModel ? "opacity: 0.5; cursor: not-allowed;" : ""}
                " onmouseover="this.style.background='${
                    !hasModel ? "white" : "#f8f9fa"
                }'" onmouseout="this.style.background='white'" ${
            !hasModel ? "disabled" : ""
        }>
                    🔄 Export training data only
                </button>
            </div>
        `;

        const choice = await this._showModal({
            title: "💼 Save / Load Text Model",
            content,
            buttons: [{ text: "Cancel", action: "cancel" }],
        });

        switch (choice) {
            case "exportModel": {
                if (!hasModel) {
                    await this._showModal({
                        title: "Cannot Export",
                        content:
                            "❌ No model data to export. Create some classes and training data first.",
                        buttons: [{ text: "OK", action: "ok" }],
                    });
                    return;
                }
                const filename = await this._showInputModal(
                    "Export Model",
                    "📝 Enter filename (without extension):",
                    "my-text-model"
                );
                if (filename && filename.trim()) {
                    this.exportModel({ FILENAME: filename.trim() });
                    await this._showModal({
                        title: "Export Complete",
                        content: "✅ Model exported successfully!",
                        buttons: [{ text: "OK", action: "ok" }],
                    });
                }
                break;
            }
            case "importModel": {
                let proceed = true;
                if (hasModel) {
                    const confirmed = await this._showModal({
                        title: "Confirm Import",
                        content:
                            "⚠️ Importing will replace your current model data. Continue?",
                        buttons: [
                            { text: "Cancel", action: "cancel" },
                            { text: "Continue", action: "confirm" },
                        ],
                    });
                    proceed = confirmed === "confirm";
                }
                if (proceed) {
                    this.importModel();
                }
                break;
            }
            case "exportTrainingData": {
                if (!hasModel) {
                    await this._showModal({
                        title: "Cannot Export",
                        content: "❌ No training data to export.",
                        buttons: [{ text: "OK", action: "ok" }],
                    });
                    return;
                }
                const filename = await this._showInputModal(
                    "Export Training Data",
                    "📝 Enter filename for training data:",
                    "training-data"
                );
                if (filename && filename.trim()) {
                    this.exportTrainingDataOnly(filename.trim());
                    await this._showModal({
                        title: "Export Complete",
                        content: "✅ Training data exported successfully!",
                        buttons: [{ text: "OK", action: "ok" }],
                    });
                }
                break;
            }
            default:
                // Cancel or invalid choice
                break;
        }
    }

    /**
     * Add training text to a classification class.
     * @param {object} args - Block arguments
     * @param {string} args.TEXT - The training text
     * @param {string} args.CLASS_NAME - The class name to add text to
     */
    addTrainingData(args) {
        const text = Cast.toString(args.TEXT);
        const className = Cast.toString(args.CLASS_NAME);

        if (!text || !className) return;

        this.addLabel(className);
        const examples = this.modelData.get(className);
        examples.push(text);

        console.log(`Added training example "${text}" to class "${className}"`);
    }

    /**
     * Train the custom text classification model.
     * @returns {Promise<void>}
     */
    async trainModel() {
        await this.buildCustomDeepModel();
    }

    /**
     * Clear all training data and reset the model.
     */
    clearTrainingData() {
        this.clearLabels();
    }

    /**
     * Export the current model and training data to a downloadable file.
     * @param {object} args - Arguments object with FILENAME property
     */
    exportModel(args) {
        const filename =
            args && args.FILENAME
                ? Cast.toString(args.FILENAME)
                : "my-text-model";

        if (this.labels.length === 0) {
            console.warn("No training data to export");
            return;
        }

        try {
            const modelData = {
                labels: this.labels,
                trainingData: Object.fromEntries(this.modelData),
                exportDate: new Date().toISOString(),
                version: "1.0",
            };

            const dataStr = JSON.stringify(modelData, null, 2);
            const dataBlob = new Blob([dataStr], { type: "application/json" });

            // Create download link
            const link = document.createElement("a");
            link.href = URL.createObjectURL(dataBlob);
            link.download = `${filename}.json`;

            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log(`Model exported as ${filename}.json`);
        } catch (error) {
            console.error("Error exporting model:", error);
        }
    }

    /**
     * Export only the training data to a file (without model state).
     * @param {string} filename - The filename for the exported data
     */
    exportTrainingDataOnly(filename) {
        if (this.labels.length === 0) {
            console.warn("No training data to export");
            return;
        }

        try {
            const trainingData = {
                labels: this.labels,
                trainingData: Object.fromEntries(this.modelData),
                exportDate: new Date().toISOString(),
                version: "1.0",
                type: "training-data-only",
            };

            const dataStr = JSON.stringify(trainingData, null, 2);
            const dataBlob = new Blob([dataStr], { type: "application/json" });

            // Create download link
            const link = document.createElement("a");
            link.href = URL.createObjectURL(dataBlob);
            link.download = `${filename}.json`;

            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log(`Training data exported as ${filename}.json`);
        } catch (error) {
            console.error("Error exporting training data:", error);
        }
    }

    /**
     * Import a model and training data from a file.
     */
    importModel() {
        try {
            // Create file input
            const fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.accept = ".json";

            fileInput.onchange = (event) => {
                const file = event.target.files[0];
                if (!file) return;

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const modelData = JSON.parse(e.target.result);

                        // Validate the imported data
                        if (!modelData.labels || !modelData.trainingData) {
                            console.error("Invalid model file format");
                            return;
                        }

                        // Import the data
                        this.labels = modelData.labels;
                        this.modelData = new Map(
                            Object.entries(modelData.trainingData)
                        );
                        this.customPredictor = null; // Reset predictor to retrain

                        const totalExamples = Array.from(
                            this.modelData.values()
                        ).reduce((sum, arr) => sum + arr.length, 0);

                        // Show success modal
                        setTimeout(() => {
                            this._showModal({
                                title: "Import Successful",
                                content: `
                                    <div style="margin-bottom: 16px;">✅ Model imported successfully!</div>
                                    <div style="display: flex; flex-direction: column; gap: 8px;">
                                        <div><strong>📝 Classes:</strong> ${this.labels.length}</div>
                                        <div><strong>📚 Training Examples:</strong> ${totalExamples}</div>
                                    </div>
                                    <div style="margin-top: 16px; font-style: italic;">
                                        You can now train the model with the imported data.
                                    </div>
                                `,
                                buttons: [{ text: "OK", action: "ok" }],
                            });
                        }, 100);

                        console.log(
                            `Model imported successfully with ${this.labels.length} classes and ${totalExamples} examples`
                        );
                    } catch (error) {
                        console.error("Error parsing model file:", error);
                    }
                };
                reader.readAsText(file);
            };

            // Trigger file selection
            fileInput.click();
        } catch (error) {
            console.error("Error importing model:", error);
        }
    }

    /**
     * Get the number of training examples for a specific class.
     * @param {object} args - Block arguments
     * @param {string} args.CLASS_NAME - The class name to count examples for
     * @returns {number} Number of training examples
     */
    getTrainingDataCount(args) {
        const className = Cast.toString(args.CLASS_NAME);

        if (!className || !this.modelData.has(className)) {
            return 0;
        }

        return this.modelData.get(className).length;
    }

    /**
     * Check if text matches a specific classification class.
     * @param {object} args - Block arguments
     * @param {string} args.TEXT - The text to classify
     * @param {string} args.CLASS_NAME - The class name to check against
     * @returns {Promise<boolean>} True if text matches the class
     */
    async ifTextMatchesClass(args) {
        const text = Cast.toString(args.TEXT);
        const className = Cast.toString(args.CLASS_NAME);

        const predictionState = await this.getEmbeddings(text);
        return predictionState && predictionState === className;
    }

    /**
     * Get predicted classification for text.
     * @param {object} args - Block arguments
     * @param {string} args.TEXT - The text to classify
     * @returns {Promise<string>} Predicted class name
     */
    async getModelPrediction(args) {
        const text = Cast.toString(args.TEXT);
        return await this.getEmbeddings(text);
    }

    /**
     * Get confidence score for text classification prediction.
     * @param {object} args - Block arguments
     * @param {string} args.TEXT - The text to classify
     * @returns {Promise<number>} Confidence score (0-100)
     */
    async getModelConfidence(args) {
        const text = Cast.toString(args.TEXT);
        const predictionConfidence = await this.getConfidence(text);
        return predictionConfidence;
    }

    /**
     * Get sentiment analysis score for text.
     * @param {object} args - Block arguments
     * @param {string} args.TEXT - The text to analyze
     * @returns {number} Sentiment score
     */
    sentimentScore(args) {
        const text = Cast.toString(args.TEXT);

        if (!this.sentimentAnalyzer || !text) {
            return 0;
        }

        try {
            this.currentSentiment = this.sentimentAnalyzer.analyze(text);
            return this.currentSentiment.comparative;
        } catch (error) {
            console.error("Error in sentiment analysis:", error);
            return 0;
        }
    }

    /**
     * Speak text using text-to-speech.
     * @param {object} args - Block arguments
     * @param {string} args.TEXT - The text to speak
     * @returns {Promise<void>}
     */
    async speakText(args) {
        const text = Cast.toString(args.TEXT);
        await this.speak(text, { currentVoice: this.currentVoice });
    }

    /**
     * Set the voice for text-to-speech.
     * @param {object} args - Block arguments
     * @param {string} args.VOICE - The voice type to use
     */
    setVoice(args) {
        const voice = Cast.toString(args.VOICE);

        // Handle number input (voice index)
        const numberInput = parseInt(JSON.stringify(voice), 10);
        if (!isNaN(numberInput)) {
            const voiceIndex = Math.min(
                Math.max(numberInput - 1, 0),
                VOICE_TYPES.length - 1
            );
            this.currentVoice = VOICE_TYPES[voiceIndex].value;
        } else {
            // Handle string input
            const found = VOICE_TYPES.find(
                ({ value, text }) => value === voice || text === voice
            );
            this.currentVoice = found ? found.value : "SQUEAK";
        }
    }

    /**
     * Speak a prompt then listen for speech input.
     * @param {object} args - Block arguments
     * @param {string} args.PROMPT - The prompt to speak
     * @returns {Promise<void>}
     */
    async askSpeechRecognition(args) {
        const prompt = Cast.toString(args.PROMPT);

        await this.speak(prompt, { currentVoice: this.currentVoice });
        await this.recognizeSpeech();
    }

    /**
     * Get the last recognized speech text.
     * @returns {string} The last recognized speech
     */
    getRecognizedSpeech() {
        return this.lastRecognizedSpeech;
    }

    /**
     * Check if microphone loudness exceeds threshold.
     * @param {object} args - Block arguments
     * @param {number} args.THRESHOLD - The loudness threshold
     * @returns {boolean} True if loudness exceeds threshold
     */
    onHeardSound(args) {
        const threshold = Cast.toNumber(args.THRESHOLD);
        return this.getLoudness() > threshold;
    }

    /**
     * Get current microphone loudness level.
     * @returns {number} Current loudness level
     */
    getLoudness() {
        const { audioEngine } = this.runtime;
        if (!audioEngine) return -1;

        try {
            this.currentLoudness = audioEngine.getLoudness();
            return this.currentLoudness;
        } catch (error) {
            return -1;
        }
    }

    // === UI Methods ===

    /**
     * Add a label to the model.
     * @param {string} label - The label to add
     */
    addLabel(label) {
        if (!this.labels.includes(label)) {
            this.labels.push(label);
            this.modelData.set(label, new Array());
        }
    }

    /**
     * Delete a label from the model.
     * @param {string} label - The label to delete
     * @param {number} index - The index of the label
     */
    deleteLabel(label, index) {
        this.labels.splice(index, 1);
        this.modelData.delete(label);
    }

    /**
     * Rename a label in the model.
     * @param {string} oldName - The old label name
     * @param {string} newName - The new label name
     * @param {number} index - The index of the label
     */
    renameLabel(oldName, newName, index) {
        if (this.modelData.has(newName)) {
            console.warn(
                `Could not rename ${oldName} to ${newName}, as that label already exists`
            );
            return;
        }
        this.labels[index] = newName;
        this.modelData.set(newName, this.modelData.get(oldName));
        this.modelData.delete(oldName);
    }

    /**
     * Clear all labels and training data.
     */
    clearLabels() {
        this.labels = [];
        this.modelData.clear();
        this.customPredictor = null;
        this.currentModelIdentifier = null;
        console.log("Cleared all training data");
    }

    /**
     * Build custom deep learning model.
     * @returns {Promise<void>}
     */
    async buildCustomDeepModel() {
        console.log("Training model...");
        const identifier = Symbol("buildCustomDeepModel");
        this.currentModelIdentifier = identifier;
        const result = await this._buildCustomModel();

        if (result && result.predict) {
            this.customPredictor = result.predict;
            console.log("The model is ready!");

            // Show success message to user
            const totalExamples = Array.from(this.modelData.values()).reduce(
                (sum, arr) => sum + arr.length,
                0
            );

            await this._showModal({
                title: "🚀 Training Complete!",
                content: `
                    <div style="text-align: center; margin-bottom: 16px;">
                        <div style="font-size: 24px; margin-bottom: 8px;">✅</div>
                        <div style="font-weight: 500; margin-bottom: 12px;">Your model is ready to use!</div>
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 8px; font-size: 14px;">
                        <div><strong>📝 Classes trained:</strong> ${this.labels.length}</div>
                        <div><strong>📚 Training examples:</strong> ${totalExamples}</div>
                    </div>
                    <div style="margin-top: 16px; padding: 12px; background: #e8f5e8; border-radius: 4px; font-size: 13px;">
                        💡 You can now use the prediction blocks to classify text!
                    </div>
                `,
                buttons: [{ text: "Start Using Model", action: "ok" }],
            });
        } else if (result && result.error) {
            console.error(result.error);

            // Show error message to user
            await this._showModal({
                title: "❌ Training Failed",
                content: `
                    <div style="margin-bottom: 16px;">
                        <strong>Error:</strong> ${result.error}
                    </div>
                    <div style="font-size: 13px; color: #666;">
                        Make sure you have at least 2 classes with training examples.
                    </div>
                `,
                buttons: [{ text: "OK", action: "ok" }],
            });
        }
    }

    // === Private Helper Methods ===

    /**
     * Initialize external libraries.
     * @private
     */
    async _initializeLibraries() {
        try {
            // Initialize sentiment analyzer
            this.sentimentAnalyzer = new Sentiment();
            console.log("Sentiment analyzer initialized");
        } catch (error) {
            console.error("Error initializing libraries:", error);
        }
    }

    /**
     * Create and show a custom modal dialog.
     * @param {object} options - Modal configuration
     * @param {string} options.title - Modal title
     * @param {string} options.content - Modal content HTML
     * @param {Array} options.buttons - Array of button objects {text, action}
     * @returns {Promise} Promise that resolves when modal is closed
     * @private
     */
    _showModal({ title, content, buttons = [] }) {
        return new Promise((resolve) => {
            // Create modal overlay
            const overlay = document.createElement("div");
            overlay.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.5);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 10000;
                font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
            `;

            // Create modal container
            const modal = document.createElement("div");
            modal.style.cssText = `
                background: #4C97FF;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                max-width: 500px;
                min-width: 350px;
                max-height: 95vh;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                position: relative;
            `;

            // Create modal header
            const header = document.createElement("div");
            header.style.cssText = `
                background: #4C97FF;
                color: white;
                padding: 16px 20px;
                font-size: 16px;
                font-weight: bold;
                position: relative;
            `;
            header.textContent = title;

            // Create close button
            const closeBtn = document.createElement("button");
            closeBtn.innerHTML = "×";
            closeBtn.style.cssText = `
                position: absolute;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
                background: none;
                border: none;
                color: white;
                font-size: 24px;
                cursor: pointer;
                padding: 0;
                width: 24px;
                height: 24px;
                line-height: 1;
            `;

            // Create modal body
            const body = document.createElement("div");
            body.style.cssText = `
                background: #f8f9fa;
                padding: 20px;
                color: #333;
                line-height: 1.5;
                flex: 1;
                overflow-y: scroll !important;
                overflow-x: hidden;
                max-height: calc(95vh - 140px);
                min-height: 200px;
                -webkit-overflow-scrolling: touch;
                scrollbar-width: thin;
            `;
            body.innerHTML = content;

            // Create button container
            const buttonContainer = document.createElement("div");
            buttonContainer.style.cssText = `
                background: #f8f9fa;
                padding: 0 20px 20px;
                display: flex;
                gap: 10px;
                justify-content: flex-end;
                flex-shrink: 0;
            `;

            // Add buttons
            buttons.forEach((btn, index) => {
                const button = document.createElement("button");
                button.textContent = btn.text;
                button.style.cssText = `
                     padding: 8px 16px;
                     border: none;
                     border-radius: 4px;
                     cursor: pointer;
                     font-size: 14px;
                     font-weight: 500;
                     transition: background-color 0.2s;
                     ${
                         index === buttons.length - 1
                             ? "background: #4C97FF; color: white;"
                             : "background: #e9ecef; color: #333;"
                     }
                 `;

                button.addEventListener("click", () => {
                    document.body.removeChild(overlay);
                    resolve(btn.action || btn.text);
                });

                buttonContainer.appendChild(button);
            });

            // Add click handlers for content buttons with data-choice
            overlay.addEventListener("click", (e) => {
                if (e.target.tagName === "BUTTON" && e.target.dataset.choice) {
                    const choice = e.target.dataset.choice;
                    document.body.removeChild(overlay);
                    resolve(choice);
                }
            });

            // Close modal function
            const closeModal = () => {
                if (document.body.contains(overlay)) {
                    document.body.removeChild(overlay);
                    resolve("cancel");
                }
            };

            closeBtn.addEventListener("click", closeModal);
            overlay.addEventListener("click", (e) => {
                if (e.target === overlay) closeModal();
            });

            // Assemble modal
            header.appendChild(closeBtn);
            modal.appendChild(header);
            modal.appendChild(body);
            if (buttons.length > 0) {
                modal.appendChild(buttonContainer);
            }
            overlay.appendChild(modal);

            // Show modal
            document.body.appendChild(overlay);
        });
    }

    /**
     * Show input modal for getting user input.
     * @param {string} title - Modal title
     * @param {string} message - Input prompt message
     * @param {string} defaultValue - Default input value
     * @returns {Promise<string|null>} User input or null if cancelled
     * @private
     */
    _showInputModal(title, message, defaultValue = "") {
        return new Promise((resolve) => {
            const content = `
                <div style="margin-bottom: 16px;">${message}</div>
                <input type="text" id="modalInput" value="${defaultValue}" style="
                    width: 100%;
                    padding: 8px 12px;
                    border: 2px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                    box-sizing: border-box;
                    margin-bottom: 4px;
                " />
            `;

            // Create custom modal that captures input before closing
            const overlay = document.createElement("div");
            overlay.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.5);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 10000;
                font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
            `;

            const modal = document.createElement("div");
            modal.style.cssText = `
                background: #4C97FF;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                max-width: 500px;
                min-width: 350px;
                max-height: 90vh;
                overflow: hidden;
                display: flex;
                flex-direction: column;
            `;

            const header = document.createElement("div");
            header.style.cssText = `
                background: #4C97FF;
                color: white;
                padding: 16px 20px;
                font-size: 16px;
                font-weight: bold;
            `;
            header.textContent = title;

            const body = document.createElement("div");
            body.style.cssText = `
                background: #f8f9fa;
                padding: 20px;
                color: #333;
                line-height: 1.5;
                flex: 1;
                overflow-y: scroll !important;
                max-height: calc(90vh - 80px);
                -webkit-overflow-scrolling: touch;
            `;
            body.innerHTML = content;

            const buttonContainer = document.createElement("div");
            buttonContainer.style.cssText = `
                background: #f8f9fa;
                padding: 0 20px 20px;
                display: flex;
                gap: 10px;
                justify-content: flex-end;
                flex-shrink: 0;
            `;

            const cancelBtn = document.createElement("button");
            cancelBtn.textContent = "Cancel";
            cancelBtn.style.cssText = `
                padding: 8px 16px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 500;
                background: #e9ecef;
                color: #333;
            `;

            const okBtn = document.createElement("button");
            okBtn.textContent = "OK";
            okBtn.style.cssText = `
                padding: 8px 16px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 500;
                background: #4C97FF;
                color: white;
            `;

            const closeModal = (result) => {
                if (document.body.contains(overlay)) {
                    document.body.removeChild(overlay);
                }
                resolve(result);
            };

            cancelBtn.addEventListener("click", () => closeModal(null));
            okBtn.addEventListener("click", () => {
                const input = document.getElementById("modalInput");
                const value = input ? input.value.trim() : "";
                closeModal(value || null);
            });

            // Handle Enter key
            overlay.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    const input = document.getElementById("modalInput");
                    const value = input ? input.value.trim() : "";
                    closeModal(value || null);
                }
            });

            buttonContainer.appendChild(cancelBtn);
            buttonContainer.appendChild(okBtn);
            modal.appendChild(header);
            modal.appendChild(body);
            modal.appendChild(buttonContainer);
            overlay.appendChild(modal);
            document.body.appendChild(overlay);

            // Focus input after a brief delay
            setTimeout(() => {
                const input = document.getElementById("modalInput");
                if (input) {
                    input.focus();
                    input.select();
                }
            }, 100);
        });
    }

    /**
     * Speak text using text-to-speech synthesis.
     * @param {string} text - Text to speak
     * @param {object} state - Voice state object
     * @returns {Promise<void>}
     * @private
     */
    async speak(text, { currentVoice }) {
        const locale = "en-US";
        const { audioEngine } = this.runtime;
        const { gender, playbackRate } = VOICE_CONFIG[currentVoice];
        const encoded = encodeURIComponent(text.substring(0, 128));
        const endpoint = `${CONFIG.SYNTHESIS_SERVICE_URL}?locale=${locale}&gender=${gender}&text=${encoded}`;

        return new Promise((resolve) => {
            const executeSpeak = async () => {
                try {
                    const response = await fetch(endpoint, { timeout: 40000 });
                    if (!response.ok) {
                        console.warn(response.statusText);
                        return resolve();
                    }
                    const buffer = await response.arrayBuffer();

                    if (audioEngine && audioEngine.decodeSoundPlayer) {
                        const soundPlayer = await audioEngine.decodeSoundPlayer(
                            {
                                data: { buffer },
                            }
                        );
                        this.soundPlayers.set(soundPlayer.id, soundPlayer);
                        soundPlayer.setPlaybackRate(playbackRate);
                        const chain = audioEngine.createEffectChain();
                        chain.set("volume", 250);
                        soundPlayer.connect(chain);
                        soundPlayer.play();
                        soundPlayer.on("stop", () => {
                            this.soundPlayers.delete(soundPlayer.id);
                            resolve();
                        });
                    } else {
                        // Fallback to basic audio playback
                        const audio = new Audio();
                        audio.src = URL.createObjectURL(new Blob([buffer]));
                        audio.playbackRate = playbackRate;
                        audio.onended = resolve;
                        audio.play();
                    }
                } catch (error) {
                    console.warn(error);
                    resolve();
                }
            };
            executeSpeak();
        });
    }

    /**
     * Recognize speech using Web Speech API.
     * @returns {Promise<string>} Recognized speech text
     * @private
     */
    async recognizeSpeech() {
        if (!window.webkitSpeechRecognition && !window.SpeechRecognition) {
            console.warn("Speech recognition not supported");
            return null;
        }

        const recognition = new (window.webkitSpeechRecognition ||
            window.SpeechRecognition)();

        const result = await new Promise((resolve) => {
            recognition.start();
            recognition.onresult = (event) => {
                resolve(
                    event.results.length === 0
                        ? null
                        : event.results[0][0].transcript
                );
            };
            recognition.onerror = () => resolve(null);
        });

        this.lastRecognizedSpeech =
            result !== null ? result : this.lastRecognizedSpeech;
        return result;
    }

    /**
     * Get confidence score for text classification.
     * @param {string} text - Text to classify
     * @returns {Promise<number>} Confidence score
     * @private
     */
    async getConfidence(text) {
        if (!this.customPredictor) return 0;

        const translation = await getTranslationToEnglish(text);
        const { score } = await this.customPredictor(translation);
        return Math.round(score * 100);
    }

    /**
     * Get embeddings/prediction for text.
     * @param {string} text - Text to classify
     * @returns {Promise<string>} Predicted label
     * @private
     */
    async getEmbeddings(text) {
        const newText = await getTranslationToEnglish(text);
        if (
            this.labels.length === 0 ||
            !this.labels[0] ||
            !this.customPredictor
        ) {
            return "";
        }

        const { label } = await this.customPredictor(newText);
        return label;
    }

    /**
     * Build custom text classification model using simple keyword matching.
     * @param {Function} isCurrent - Function to check if training is still current
     * @returns {Promise<object|null>} Trained model predictor
     * @private
     */
    async _buildCustomModel() {
        const { length } = this.labels;

        if (length < CONFIG.MIN_CLASSES) {
            return { error: "2 or more classes required" };
        }

        try {
            console.log("Building keyword-based text classification model...");

            // Create predictor function using keyword matching
            const predictor = async (text) => {
                if (this.labels.length === 0) {
                    return { label: "", score: 0, index: -1 };
                }

                // Simple keyword matching
                const lowerText = text.toLowerCase();
                let bestMatch = this.labels[0];
                let bestScore = 0;
                let bestIndex = 0;

                for (let i = 0; i < this.labels.length; i++) {
                    const label = this.labels[i];
                    const examples = this.modelData.get(label) || [];

                    let score = 0;

                    if (examples.length === 0) {
                        // If no training examples, check if text contains the label name
                        if (lowerText.includes(label.toLowerCase())) {
                            score = 0.5;
                        }
                    } else {
                        // Calculate similarity based on training examples
                        for (const example of examples) {
                            const exampleWords = example
                                .toLowerCase()
                                .split(/\s+/);
                            const textWords = lowerText.split(/\s+/);

                            // Count common words
                            const commonWords = exampleWords.filter(
                                (word) =>
                                    textWords.includes(word) && word.length > 2
                            );

                            if (
                                exampleWords.length > 0 &&
                                textWords.length > 0
                            ) {
                                score +=
                                    commonWords.length /
                                    Math.max(
                                        exampleWords.length,
                                        textWords.length
                                    );
                            }
                        }
                        score = score / examples.length; // Average score across all examples
                    }

                    if (score > bestScore) {
                        bestScore = score;
                        bestMatch = label;
                        bestIndex = i;
                    }
                }

                return {
                    label: bestMatch,
                    score: Math.min(bestScore, 1),
                    index: bestIndex,
                };
            };

            return { predict: predictor };
        } catch (error) {
            console.error("Error building custom model:", error);
            return { error: error.toString() };
        }
    }

    /**
     * Register custom button callbacks.
     * @private
     */
    _registerButtonCallbacks() {
        // Register button callbacks with the runtime
        this.runtime.emit(
            "REGISTER_BUTTON_CALLBACK_FROM_EXTENSION",
            "EDIT_TEXT_MODEL"
        );
        this.runtime.emit(
            "REGISTER_BUTTON_CALLBACK_FROM_EXTENSION",
            "SAVE_LOAD_TEXT_MODEL"
        );

        // Listen for button click events
        this.runtime.on("EDIT_TEXT_MODEL", () => {
            this.editModelButton();
        });

        this.runtime.on("SAVE_LOAD_TEXT_MODEL", () => {
            this.saveLoadModelButton();
        });
    }
}

module.exports = Scratch3TextClassificationBlocks;
